<template>
  <q-page class="admin-users-page">
    <div class="admin-users-container">
      <!-- Header -->
      <div class="au-header">
        <router-link to="/admin" class="au-back-btn">
          <q-icon name="arrow_back" size="20px" />
        </router-link>
        <div class="au-header-info">
          <h1 class="au-title">Gestión de Usuarios</h1>
          <p class="au-subtitle">{{ total }} usuarios registrados</p>
        </div>
        <q-btn
          no-caps
          unelevated
          label="Crear alumno"
          icon="person_add"
          style="background: var(--oa-gold-full); color: var(--color-white-cap); font-weight: 600; border-radius: 8px;"
          @click="showCreateDialog = true"
        />
      </div>

      <!-- Filters -->
      <div class="au-filters">
        <q-input
          v-model="search"
          dense
          outlined
          dark
          placeholder="Buscar por nombre o email..."
          class="au-search"
          @update:model-value="onSearchChange"
        >
          <template #prepend>
            <q-icon name="search" size="18px" style="color: var(--oa-text-muted);" />
          </template>
        </q-input>
        <q-select
          v-model="roleFilter"
          :options="roleFilterOptions"
          emit-value
          map-options
          dense
          outlined
          dark
          class="au-role-filter"
          @update:model-value="onFilterChange"
        />
      </div>

      <!-- Users list -->
      <div class="au-list-card">
        <!-- Loading -->
        <div v-if="loading" class="au-center-msg">Cargando...</div>

        <!-- Error -->
        <div v-else-if="error" class="au-center-msg">
          <p style="color: var(--oa-danger); margin-bottom: 8px;">{{ error }}</p>
          <q-btn flat no-caps label="Reintentar" style="color: var(--oa-gold-full);" @click="fetchUsers" />
        </div>

        <!-- Empty -->
        <div v-else-if="users.length === 0" class="au-center-msg">
          No se encontraron usuarios
        </div>

        <!-- User rows -->
        <template v-else>
          <div
            v-for="user in users"
            :key="user.id"
            class="au-user-row"
            @click="goToFicha(user.id)"
          >
            <div class="au-user-left">
              <div class="au-avatar" :class="{ 'au-avatar-inactive': !user.isActive }">
                {{ initials(user.firstName, user.lastName) }}
              </div>
              <div class="au-user-info">
                <p class="au-user-name">
                  {{ user.firstName }} {{ user.lastName }}
                  <span v-if="!user.isActive" class="au-inactive-tag">(desactivado)</span>
                </p>
                <p class="au-user-email">{{ user.email }}</p>
              </div>
            </div>

            <div class="au-user-right" @click.stop>
              <!-- Role (inline edit) -->
              <template v-if="editingUserId === user.id">
                <div class="au-role-edit">
                  <q-select
                    v-model="newRole"
                    :options="allRoleOptions"
                    emit-value
                    map-options
                    dense
                    outlined
                    dark
                    style="min-width: 140px;"
                  />
                  <q-btn
                    dense
                    no-caps
                    unelevated
                    label="OK"
                    :loading="roleChanging"
                    :disable="!newRole"
                    style="background: var(--oa-gold-full); color: var(--color-white-cap); font-weight: 600; border-radius: 6px; font-size: 12px;"
                    @click="changeRole(user.id)"
                  />
                  <q-btn
                    dense
                    flat
                    no-caps
                    label="X"
                    style="color: var(--oa-text-muted); font-size: 12px;"
                    @click="editingUserId = null"
                  />
                </div>
              </template>
              <template v-else>
                <button class="au-role-badge" @click="startEdit(user.id, user.role)">
                  {{ roleLabels[user.role] || user.role }}
                </button>
              </template>

              <!-- Toggle active -->
              <q-btn
                flat
                round
                dense
                :icon="user.isActive ? 'person' : 'person_off'"
                :style="{ color: user.isActive ? 'var(--oa-success)' : 'var(--oa-danger)' }"
                :title="user.isActive ? 'Desactivar usuario' : 'Activar usuario'"
                :loading="togglingId === user.id"
                @click="toggleActive(user.id, user.isActive)"
              />
            </div>
          </div>
        </template>

        <!-- Pagination -->
        <div v-if="totalPages > 1" class="au-pagination">
          <span class="au-page-info">Página {{ page }} de {{ totalPages }}</span>
          <div class="au-page-btns">
            <q-btn
              flat
              round
              dense
              icon="chevron_left"
              :disable="page <= 1"
              style="color: var(--oa-text-muted);"
              @click="goPage(page - 1)"
            />
            <q-btn
              flat
              round
              dense
              icon="chevron_right"
              :disable="page >= totalPages"
              style="color: var(--oa-text-muted);"
              @click="goPage(page + 1)"
            />
          </div>
        </div>
      </div>

      <!-- Role change dialog -->
      <q-dialog v-model="showRoleDialog" persistent>
        <q-card style="min-width: 350px; background: var(--oa-bg-2); border: 1px solid var(--oa-border);">
          <q-card-section>
            <div class="text-h6" style="color: var(--oa-text-primary);">Cambiar rol</div>
          </q-card-section>
          <q-card-section>
            <q-select
              v-model="dialogRole"
              :options="allRoleOptions"
              emit-value
              map-options
              dense
              outlined
              dark
              label="Nuevo rol"
            />
            <q-input
              v-model="dialogReason"
              dense
              outlined
              dark
              label="Motivo (opcional)"
              style="margin-top: 12px;"
            />
          </q-card-section>
          <q-card-actions align="right">
            <q-btn flat no-caps label="Cancelar" style="color: var(--oa-text-muted);" @click="showRoleDialog = false" />
            <q-btn
              no-caps
              unelevated
              label="Confirmar"
              :loading="roleChanging"
              :disable="!dialogRole"
              style="background: var(--oa-gold-full); color: var(--color-white-cap); font-weight: 600; border-radius: 8px;"
              @click="confirmDialogRole"
            />
          </q-card-actions>
        </q-card>
      </q-dialog>

      <!-- Crear alumno dialog -->
      <q-dialog v-model="showCreateDialog" persistent @hide="resetCreateForm">
        <q-card style="min-width: 350px; background: var(--oa-bg-2); border: 1px solid var(--oa-border);">
          <q-card-section>
            <div class="text-h6" style="color: var(--oa-text-primary);">Crear alumno</div>
          </q-card-section>
          <q-card-section>
            <q-input
              v-model="createForm.firstName"
              dense
              outlined
              dark
              label="Nombre"
              style="margin-bottom: 12px;"
            />
            <q-input
              v-model="createForm.lastName"
              dense
              outlined
              dark
              label="Apellido"
              style="margin-bottom: 12px;"
            />
            <q-input
              v-model="createForm.email"
              dense
              outlined
              dark
              type="email"
              label="Email"
              style="margin-bottom: 12px;"
            />
            <q-input
              v-model="createForm.password"
              dense
              outlined
              dark
              type="password"
              label="Contraseña"
              hint="Mínimo 8 caracteres"
            />
          </q-card-section>
          <q-card-actions align="right">
            <q-btn flat no-caps label="Cancelar" style="color: var(--oa-text-muted);" @click="showCreateDialog = false" />
            <q-btn
              no-caps
              unelevated
              label="Crear"
              :loading="creating"
              style="background: var(--oa-gold-full); color: var(--color-white-cap); font-weight: 600; border-radius: 8px;"
              @click="confirmCreateStudent"
            />
          </q-card-actions>
        </q-card>
      </q-dialog>
    </div>
  </q-page>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { api } from 'boot/axios';
import { useQuasar } from 'quasar';

const $q = useQuasar();
const router = useRouter();

// ---------- Types ----------
interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  isActive: boolean;
  createdAt: string;
}

// ---------- State ----------
const loading = ref(true);
const error = ref<string | null>(null);
const users = ref<User[]>([]);
const total = ref(0);
const page = ref(1);
const totalPages = ref(1);
const search = ref('');
const roleFilter = ref('');
const editingUserId = ref<string | null>(null);
const newRole = ref('');
const roleChanging = ref(false);
const togglingId = ref<string | null>(null);

// Dialog
const showRoleDialog = ref(false);
const dialogUserId = ref<string | null>(null);
const dialogRole = ref('');
const dialogReason = ref('');

// Crear alumno dialog
const showCreateDialog = ref(false);
const creating = ref(false);
const createForm = ref({
  firstName: '',
  lastName: '',
  email: '',
  password: '',
});

// ---------- Constants ----------
const roleLabels: Record<string, string> = {
  admin: 'Administrador',
  alumno: 'Alumno',
};

const allRoleOptions = Object.entries(roleLabels).map(([value, label]) => ({ label, value }));

const roleFilterOptions = [
  { label: 'Todos los roles', value: '' },
  ...allRoleOptions,
];

// ---------- Helpers ----------
function initials(first: string, last: string): string {
  return (first.charAt(0) + last.charAt(0)).toUpperCase();
}

let searchTimeout: ReturnType<typeof setTimeout> | null = null;

function onSearchChange() {
  if (searchTimeout) clearTimeout(searchTimeout);
  searchTimeout = setTimeout(() => {
    page.value = 1;
    fetchUsers();
  }, 300);
}

function onFilterChange() {
  page.value = 1;
  fetchUsers();
}

function goPage(p: number) {
  page.value = p;
  fetchUsers();
}

function startEdit(userId: string, currentRole: string) {
  editingUserId.value = userId;
  newRole.value = currentRole;
}

function goToFicha(userId: string) {
  router.push({ name: 'admin-student-ficha', params: { userId } });
}

// ---------- Data Fetching ----------
async function fetchUsers() {
  loading.value = true;
  error.value = null;
  try {
    const params: Record<string, string | number> = { page: page.value, limit: 20 };
    if (search.value) params.search = search.value;
    if (roleFilter.value) params.role = roleFilter.value;

    const { data: res } = await api.get('/admin/users', { params });
    users.value = res.items || [];
    total.value = res.total || 0;
    totalPages.value = res.totalPages || 1;
    page.value = res.page || 1;
  } catch (err: unknown) {
    const msg = err instanceof Error ? err.message : 'Error al cargar usuarios';
    error.value = msg;
  } finally {
    loading.value = false;
  }
}

async function changeRole(userId: string) {
  if (!newRole.value) return;
  roleChanging.value = true;
  try {
    await api.post(`/admin/users/${userId}/role`, { newRole: newRole.value });
    editingUserId.value = null;
    newRole.value = '';
    $q.notify({ type: 'positive', message: 'Rol actualizado', color: 'positive' });
    fetchUsers();
  } catch (err: unknown) {
    const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
    $q.notify({ type: 'negative', message: msg || 'Error al cambiar rol' });
  } finally {
    roleChanging.value = false;
  }
}

async function confirmDialogRole() {
  if (!dialogUserId.value || !dialogRole.value) return;
  roleChanging.value = true;
  try {
    await api.post(`/admin/users/${dialogUserId.value}/role`, {
      newRole: dialogRole.value,
      reason: dialogReason.value || undefined,
    });
    showRoleDialog.value = false;
    dialogUserId.value = null;
    dialogRole.value = '';
    dialogReason.value = '';
    $q.notify({ type: 'positive', message: 'Rol actualizado', color: 'positive' });
    fetchUsers();
  } catch (err: unknown) {
    const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
    $q.notify({ type: 'negative', message: msg || 'Error al cambiar rol' });
  } finally {
    roleChanging.value = false;
  }
}

function resetCreateForm() {
  createForm.value = { firstName: '', lastName: '', email: '', password: '' };
}

async function confirmCreateStudent() {
  const f = createForm.value;
  const firstName = f.firstName.trim();
  const lastName = f.lastName.trim();
  const email = f.email.trim();

  // Validacion en cliente: refleja las reglas del backend para dar feedback
  // inmediato y evitar el 400 generico enmascarado como "Error al crear alumno".
  if (!firstName || !lastName || !email || !f.password) {
    $q.notify({ type: 'negative', message: 'Completá todos los campos' });
    return;
  }
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    $q.notify({ type: 'negative', message: 'Ingresá un email válido' });
    return;
  }
  if (f.password.length < 8) {
    $q.notify({ type: 'negative', message: 'La contraseña debe tener al menos 8 caracteres' });
    return;
  }

  creating.value = true;
  try {
    await api.post('/admin/users', { firstName, lastName, email, password: f.password });
    showCreateDialog.value = false;
    resetCreateForm();
    $q.notify({ type: 'positive', message: 'Alumno creado correctamente' });
    fetchUsers();
  } catch (err: unknown) {
    const response = (err as { response?: { status?: number; data?: { message?: string } } })?.response;
    const status = response?.status;
    if (status === 409) {
      $q.notify({ type: 'negative', message: 'El email ya esta registrado' });
    } else {
      // Muestra el mensaje real del backend si existe, en vez del generico.
      $q.notify({ type: 'negative', message: response?.data?.message || 'Error al crear alumno' });
    }
  } finally {
    creating.value = false;
  }
}

async function toggleActive(userId: string, currentlyActive: boolean) {
  togglingId.value = userId;
  try {
    await api.patch(`/admin/users/${userId}/active`);
    // Optimistic update
    const user = users.value.find((u) => u.id === userId);
    if (user) user.isActive = !currentlyActive;
    $q.notify({
      type: 'info',
      message: currentlyActive ? 'Usuario desactivado' : 'Usuario activado',
    });
  } catch {
    $q.notify({ type: 'negative', message: 'Error al cambiar estado' });
    fetchUsers(); // Rollback on error
  } finally {
    togglingId.value = null;
  }
}

// ---------- Lifecycle ----------
onMounted(() => {
  fetchUsers();
});
</script>

<style scoped>
.admin-users-page {
  padding: 24px;
}

.admin-users-container {
  max-width: 1000px;
  margin: 0 auto;
}

/* Header */
.au-header {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
}

.au-back-btn {
  padding: 8px;
  border-radius: 12px;
  color: var(--oa-text-muted);
  transition: background 0.15s ease;
  text-decoration: none;
}

.au-back-btn:hover {
  background: var(--oa-bg-3);
}

.au-header-info {
  flex: 1;
}

.au-title {
  font-size: 1.75rem;
  font-weight: 700;
  color: var(--oa-text-primary);
  margin: 0;
}

.au-subtitle {
  font-size: 0.875rem;
  color: var(--oa-text-muted);
  margin: 2px 0 0;
}

/* Filters */
.au-filters {
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
}

.au-search {
  flex: 1;
}

.au-role-filter {
  min-width: 180px;
}

@media (max-width: 600px) {
  .au-filters {
    flex-direction: column;
  }
  .au-role-filter {
    min-width: 100%;
  }
}

/* List card */
.au-list-card {
  background: var(--oa-bg-2);
  border: 1px solid var(--oa-border);
  border-radius: 16px;
  overflow: hidden;
}

.au-center-msg {
  padding: 32px;
  text-align: center;
  color: var(--oa-text-muted);
  font-size: 0.875rem;
}

/* User row */
.au-user-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  border-bottom: 1px solid color-mix(in srgb, var(--oa-border) 50%, transparent);
  transition: background 0.1s ease;
  cursor: pointer;
}

.au-user-row:hover {
  background: color-mix(in srgb, var(--oa-bg-3) 30%, transparent);
}

.au-user-row:last-child {
  border-bottom: none;
}

.au-user-left {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.au-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: color-mix(in srgb, var(--oa-gold-full) 15%, transparent);
  color: var(--oa-gold-full);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 700;
  flex-shrink: 0;
}

.au-avatar-inactive {
  background: var(--oa-bg-3);
  color: var(--oa-text-disabled);
}

.au-user-info {
  min-width: 0;
}

.au-user-name {
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--oa-text-primary);
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.au-inactive-tag {
  font-size: 0.75rem;
  color: var(--oa-danger);
  margin-left: 4px;
}

.au-user-email {
  font-size: 0.6875rem;
  color: var(--oa-text-muted);
  margin: 2px 0 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.au-user-right {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

/* Role badge */
.au-role-badge {
  font-size: 0.6875rem;
  padding: 3px 10px;
  border-radius: 12px;
  background: var(--oa-bg-3);
  color: var(--oa-text-secondary);
  font-weight: 500;
  border: none;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}

.au-role-badge:hover {
  background: color-mix(in srgb, var(--oa-gold-full) 15%, transparent);
  color: var(--oa-gold-full);
}

/* Role edit inline */
.au-role-edit {
  display: flex;
  align-items: center;
  gap: 8px;
}

/* Pagination */
.au-pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  border-top: 1px solid color-mix(in srgb, var(--oa-border) 50%, transparent);
}

.au-page-info {
  font-size: 0.75rem;
  color: var(--oa-text-muted);
}

.au-page-btns {
  display: flex;
  align-items: center;
  gap: 4px;
}
</style>
